import {
  useAtsCommTemplateVariables,
  type AtsCommTemplateVariables,
} from '@/api/atsCommTemplates';
import {
  CANDIDATE_CALENDAR_TEMPLATE_CATEGORIES,
  CANDIDATE_EMAIL_TEMPLATE_CATEGORIES,
  INTERVIEWER_CALENDAR_TEMPLATE_CATEGORIES,
  INTERVIEWER_EMAIL_TEMPLATE_CATEGORIES,
  SELF_SCHEDULE_EMAIL_TEMPLATE_CATEGORIES,
  CANDIDATE_AVAILABILITY_EMAIL_TEMPLATE_CATEGORIES,
} from './interviewCommunicationQueries';

type TemplateVariableView =
  | 'sender'
  | 'interview'
  | 'job'
  | 'candidate'
  | 'org'
  | 'offer'
  | 'survey'
  | 'user'
  | 'schedule_interview'
  | 'candidate_availability';

/**
 * Local category -> variables view mapping for InterviewActivity communication.
 * Source of truth mirrors template settings config, but intentionally maintained
 * in this feature for stability and explicitness.
 */
const CATEGORY_VARIABLE_VIEWS: Record<string, readonly TemplateVariableView[]> =
  {
    candidate_adhoc: ['sender', 'candidate', 'org', 'job'],
    candidate_interview_notice: [
      'sender',
      'interview',
      'job',
      'candidate',
      'org',
    ],
    candidate_job_stage_action_notice: ['sender', 'candidate', 'org', 'job'],
    candidate_application_received: ['job', 'candidate', 'org'],
    candidate_assessment_notice: ['sender', 'candidate', 'org', 'job'],
    candidate_application_rejected: [
      'sender',
      'job',
      'candidate',
      'org',
      'survey',
    ],
    candidate_offer_notice: ['sender', 'candidate', 'org', 'job', 'offer'],
    candidate_interview_confirmation: [
      'sender',
      'interview',
      'job',
      'candidate',
      'org',
    ],
    interviewer_notice: ['sender', 'interview', 'job', 'candidate', 'org'],
    candidate_availability_notice: [
      'sender',
      'candidate',
      'org',
      'job',
      'interview',
      'candidate_availability',
    ],
    interviewer_interview_confirmation: [
      'sender',
      'interview',
      'job',
      'candidate',
      'org',
    ],
    candidate_schedule_notice: [
      'sender',
      'schedule_interview',
      'job',
      'candidate',
      'org',
    ],
    survey_notice: ['sender', 'candidate', 'org', 'job', 'survey', 'user'],
  };

const DEFAULT_VARIABLE_VIEWS: readonly TemplateVariableView[] = [
  'sender',
  'interview',
  'job',
  'candidate',
  'org',
];

const resolveViewsForCategories = (
  categories: readonly string[]
): readonly TemplateVariableView[] => {
  const views = categories.flatMap(
    (category) => CATEGORY_VARIABLE_VIEWS[category] ?? DEFAULT_VARIABLE_VIEWS
  );
  return Array.from(new Set(views));
};

const CANDIDATE_CALENDAR_VIEWS = resolveViewsForCategories(
  CANDIDATE_CALENDAR_TEMPLATE_CATEGORIES
);
const INTERVIEWER_CALENDAR_VIEWS = resolveViewsForCategories(
  INTERVIEWER_CALENDAR_TEMPLATE_CATEGORIES
);
const CANDIDATE_EMAIL_VIEWS = resolveViewsForCategories(
  CANDIDATE_EMAIL_TEMPLATE_CATEGORIES
);
const INTERVIEWER_EMAIL_VIEWS = resolveViewsForCategories(
  INTERVIEWER_EMAIL_TEMPLATE_CATEGORIES
);
const SELF_SCHEDULE_EMAIL_VIEWS = resolveViewsForCategories(
  SELF_SCHEDULE_EMAIL_TEMPLATE_CATEGORIES
);
const CANDIDATE_AVAILABILITY_EMAIL_VIEWS = resolveViewsForCategories(
  CANDIDATE_AVAILABILITY_EMAIL_TEMPLATE_CATEGORIES
);

/**
 * Merge-variable buckets for each Communication block.
 * Each block fetches its own set of variables based on the view scopes above.
 */
export function useInterviewActivityCommunicationVariables(): {
  candidateCalendar: AtsCommTemplateVariables;
  interviewerCalendar: AtsCommTemplateVariables;
  candidateEmail: AtsCommTemplateVariables;
  interviewerEmail: AtsCommTemplateVariables;
  selfScheduleEmail: AtsCommTemplateVariables;
  candidateAvailabilityEmail: AtsCommTemplateVariables;
} {
  const { data: candidateCalendar = {} } = useAtsCommTemplateVariables(
    CANDIDATE_CALENDAR_VIEWS
  );
  const { data: interviewerCalendar = {} } = useAtsCommTemplateVariables(
    INTERVIEWER_CALENDAR_VIEWS
  );
  const { data: candidateEmail = {} } = useAtsCommTemplateVariables(
    CANDIDATE_EMAIL_VIEWS
  );
  const { data: interviewerEmail = {} } = useAtsCommTemplateVariables(
    INTERVIEWER_EMAIL_VIEWS
  );
  const { data: selfScheduleEmail = {} } = useAtsCommTemplateVariables(
    SELF_SCHEDULE_EMAIL_VIEWS
  );
  const { data: candidateAvailabilityEmail = {} } = useAtsCommTemplateVariables(
    CANDIDATE_AVAILABILITY_EMAIL_VIEWS
  );

  return {
    candidateCalendar,
    interviewerCalendar,
    candidateEmail,
    interviewerEmail,
    selfScheduleEmail,
    candidateAvailabilityEmail,
  };
}
